import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from './ui/Button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** Compact variant for embedded widgets like the chat preview. */
  compact?: boolean;
}

interface State {
  hasError: boolean;
  message?: string;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, message: error.message };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, info);
  }

  reset = () => this.setState({ hasError: false, message: undefined });

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    if (this.props.compact) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
          <AlertTriangle className="h-6 w-6 text-warning" />
          <p className="text-sm text-mid">This panel hit an error.</p>
          <Button size="sm" variant="outline" onClick={this.reset}>
            <RefreshCw className="h-3.5 w-3.5" /> Retry
          </Button>
        </div>
      );
    }

    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-8 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-light">
          <AlertTriangle className="h-7 w-7 text-warning" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-primary">Something went wrong</h2>
          <p className="mt-1 max-w-sm text-sm text-mid">
            {this.state.message ?? 'An unexpected error occurred while rendering this page.'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={this.reset}>
            <RefreshCw className="h-4 w-4" /> Try again
          </Button>
          <Button onClick={() => window.location.assign('/')}>Go to Dashboard</Button>
        </div>
      </div>
    );
  }
}
